import { useNavigate } from 'react-router';
import { ClientFormDialog } from '../../features/clients';
import { NewNoteDialog } from '../../features/knowledge';
import { AddAssetDialog } from '../../features/maintenance';
import { NewMeetingDialog } from '../../features/meetings';
import { NewOfferDialog } from '../../features/offers';
import { ProjectFormDialog } from '../../features/projects';
import { NewTaskDialog } from '../../features/tasks';
import { AddTimeDialog } from '../../features/time';
import { useAccess } from '../../lib/useAccess';
import type { AddDialogKind } from './nav';
import type { AddDialogDefaults } from './shell-context';

export interface AddDialogState {
  kind: AddDialogKind;
  defaults: AddDialogDefaults;
}

/**
 * Formularze „Dodaj…” otwierane z ⌘K i przycisku „+” w menu (AppShell): dialogi modułów zadań, czasu, klientów,
 * projektów, Utrzymania, wiedzy, spotkań i ofert. Po zapisie klienta, projektu, zasobu, notatki, spotkania i oferty
 * przechodzimy do jego karty; zadanie i wpis czasu pokazują toast (domyślne zachowanie dialogów). Bez uprawnień do
 * edycji modułu dialog się nie pokazuje (spotkania i oferty należą do modułu Klienci).
 */
export function AddDialogs({ state, onClose }: { state: AddDialogState | null; onClose: () => void }) {
  const access = useAccess();
  const navigate = useNavigate();
  const onOpenChange = (open: boolean) => {
    if (!open) onClose();
  };
  const kind = state?.kind ?? null;
  const defaults = state?.defaults ?? {};

  return (
    <>
      {access.can('tasks', 'edit') && (
        <NewTaskDialog
          open={kind === 'task'}
          onOpenChange={onOpenChange}
          defaultClientId={defaults.clientId ?? null}
          defaultProjectId={defaults.projectId ?? null}
        />
      )}
      {access.can('time', 'edit') && kind === 'time' && (
        <AddTimeDialog
          open
          onOpenChange={onOpenChange}
          defaultClientId={defaults.clientId ?? null}
          defaultProjectId={defaults.projectId ?? null}
        />
      )}
      {access.can('clients', 'edit') && (
        <ClientFormDialog
          open={kind === 'client'}
          onOpenChange={onOpenChange}
          onSaved={(client) => void navigate(`/klienci/${client.id}`)}
        />
      )}
      {access.can('projects', 'edit') && (
        <ProjectFormDialog
          open={kind === 'project'}
          onOpenChange={onOpenChange}
          defaultClientId={defaults.clientId ?? null}
          onSaved={(project) => void navigate(`/projekty/${project.id}`)}
        />
      )}
      {access.can('maintenance', 'edit') && (
        <AddAssetDialog
          open={kind === 'asset'}
          onOpenChange={onOpenChange}
          defaultClientId={defaults.clientId ?? null}
          onSaved={(asset) => void navigate(`/utrzymanie/${asset.id}`)}
        />
      )}
      {access.can('knowledge', 'edit') && kind === 'note' && (
        <NewNoteDialog
          open
          onOpenChange={onOpenChange}
          defaultClientId={defaults.clientId ?? null}
          defaultProjectId={defaults.projectId ?? null}
        />
      )}
      {access.can('clients', 'edit') && kind === 'meeting' && (
        <NewMeetingDialog
          open
          onOpenChange={onOpenChange}
          defaultClientId={defaults.clientId ?? null}
          defaultProjectId={defaults.projectId ?? null}
          onSaved={(meeting) => void navigate(`/spotkania/${meeting.id}`)}
        />
      )}
      {access.can('clients', 'edit') && kind === 'offer' && (
        <NewOfferDialog open onOpenChange={onOpenChange} defaultClientId={defaults.clientId ?? null} />
      )}
    </>
  );
}
